import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Dropdown } from 'antd'
import moment from 'moment'
import classNames from 'classnames'
import chevronDownIcon from '@/assets/images/chevron-down-24.svg'
import styles from './style.module.scss'

const TimeSelector = ({
  value,
  onChange,
  placeholder = 'HH:mm',
  disabled = false,
  className,
  interval = 30,
  selectedDate,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef(null)
  const isDeletingRef = useRef(false)

  const generateTimeOptions = () => {
    const options = []
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += interval) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        options.push(timeStr)
      }
    }
    return options
  }

  const timeOptions = generateTimeOptions()

  useEffect(() => {
    if (value) {
      let timeStr = ''
      if (value instanceof Date) {
        const hours = value.getHours().toString().padStart(2, '0')
        const minutes = value.getMinutes().toString().padStart(2, '0')
        timeStr = `${hours}:${minutes}`
      } else if (typeof value === 'string') {
        timeStr = value
      }

      // selectedDate가 오늘이고 value가 현재 시간보다 과거라면 현재 시간으로 변경
      if (timeStr && isToday() && isTimeBeforeNow(timeStr)) {
        const currentTime = getCurrentTime()
        setInputValue(currentTime)
        if (onChange != null) {
          onChange(currentTime)
        }
      } else {
        setInputValue(timeStr)
      }
    } else {
      setInputValue('')
    }
  }, [value, selectedDate]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatTimeInput = (input) => {
    const numbers = input.replace(/[^0-9]/g, '')

    if (numbers.length === 0) return ''
    if (numbers.length === 4) {
      const hours = numbers.slice(0, 2)
      const minutes = numbers.slice(2, 4)
      return `${hours}:${minutes}`
    }

    return numbers
  }

  const isToday = () => {
    const selected = selectedDate ? moment(selectedDate) : moment()
    const today = moment()
    return selected.isSame(today, 'day')
  }

  const roundUpToInterval = (m) => {
    const minutes = m.minutes()
    const remainder = minutes % interval
    if (remainder === 0) {
      return m.clone()
    }
    return m.clone().add(interval - remainder, 'minutes').seconds(0)
  }

  const getCurrentTime = () => {
    const now = moment()
    const rounded = roundUpToInterval(now)
    const hours = rounded.hours().toString().padStart(2, '0')
    const minutes = rounded.minutes().toString().padStart(2, '0')

    return `${hours}:${minutes}`
  }

  const isTimeBeforeNow = (timeStr) => {
    if (!isToday()) return false

    const now = moment()
    const [timeHour, timeMinute] = timeStr.split(':').map(Number)
    const timeMoment = moment().hours(timeHour).minutes(timeMinute).seconds(0)

    return timeMoment.isSameOrBefore(now)
  }

  const validateAndFormatTime = (timeStr) => {
    if (!timeStr) return null

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    if (timeRegex.test(timeStr)) {
      const [hours, minutes] = timeStr.split(':')
      const formattedHours = parseInt(hours, 10).toString().padStart(2, '0')
      const formattedMinutes = parseInt(minutes, 10).toString().padStart(2, '0')
      const formatted = `${formattedHours}:${formattedMinutes}`

      if (isTimeBeforeNow(formatted)) {
        return null
      }

      return formatted
    }
    return null
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value

    if (isDeletingRef.current) {
      isDeletingRef.current = false
      setInputValue(newValue)

      if (!newValue) {
        if (onChange != null) {
          onChange('')
        }
        return
      }

      const validated = validateAndFormatTime(newValue)
      if (validated) {
        const next = isTimeBeforeNow(validated) ? getCurrentTime() : validated
        setInputValue(next)
        if (onChange != null) {
          onChange(next)
        }
      }
      return
    }

    if (!newValue.includes(':') && newValue.length > 0) {
      const numbers = newValue.replace(/[^0-9]/g, '')

      if (numbers.length === 4) {
        const formatted = formatTimeInput(newValue)
        setInputValue(formatted)

        const validated = validateAndFormatTime(formatted)
        if (validated) {
          const next = isTimeBeforeNow(validated) ? getCurrentTime() : validated
          setInputValue(next)
          if (onChange != null) {
            onChange(next)
          }
        }
        return
      }

      setInputValue(newValue)
      return
    }

    setInputValue(newValue)

    const validated = validateAndFormatTime(newValue)
    if (validated) {
      const next = isTimeBeforeNow(validated) ? getCurrentTime() : validated
      setInputValue(next)
      if (onChange != null) {
        onChange(next)
      }
    }
  }

  const handleInputKeyDown = (e) => {
    console.log(' --- e --- ', e.key)

    if (e.key === 'Backspace' || e.key === 'Delete') {
      isDeletingRef.current = true
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()

      if (!inputValue || inputValue.trim() === '') {
        setIsOpen(false)
        inputRef.current?.blur()
        return
      }

      const validated = validateAndFormatTime(inputValue)

      if (validated) {
        const nextValue = isTimeBeforeNow(validated) ? getCurrentTime() : validated
        setInputValue(nextValue)
        onChange?.(nextValue)
        setIsOpen(false)
        inputRef.current?.blur()
        return
      }

      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
      if (timeRegex.test(inputValue)) {
        const [hours, minutes] = inputValue.split(':')
        const formatted = `${parseInt(hours, 10).toString().padStart(2, '0')}:${parseInt(minutes, 10).toString().padStart(2, '0')}`
        const nextValue = isTimeBeforeNow(formatted) ? getCurrentTime() : formatted
        setInputValue(nextValue)
        onChange?.(nextValue)
        setIsOpen(false)
        inputRef.current?.blur()
        return
      }

      if (value) {
        if (value instanceof Date) {
          const hours = value.getHours().toString().padStart(2, '0')
          const minutes = value.getMinutes().toString().padStart(2, '0')
          setInputValue(`${hours}:${minutes}`)
        } else {
          setInputValue(value)
        }
      } else {
        setInputValue('')
      }
      setIsOpen(false)
      inputRef.current?.blur()
      return
    }

    if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  const handleInputBlur = () => {
    if (!inputValue) {
      return
    }

    const validated = validateAndFormatTime(inputValue)
    if (validated) {
      const nextValue = isTimeBeforeNow(validated) ? getCurrentTime() : validated
      setInputValue(nextValue)
      onChange?.(nextValue)
      return
    }

    if (value) {
      if (value instanceof Date) {
        const hours = value.getHours().toString().padStart(2, '0')
        const minutes = value.getMinutes().toString().padStart(2, '0')
        setInputValue(`${hours}:${minutes}`)
      } else {
        setInputValue(value)
      }
    } else {
      setInputValue('')
    }
  }

  const handleTimeSelect = ({ key }) => {
    const nextValue = isTimeBeforeNow(key) ? getCurrentTime() : key
    setInputValue(nextValue)
    onChange?.(nextValue)
    setIsOpen(false)
  }

  const getNextAvailableTime = () => {
    return getCurrentTime()
  }

  const prevDateRef = useRef(null)
  useEffect(() => {
    const nextDateStr = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null
    if (prevDateRef.current !== nextDateStr) {
      prevDateRef.current = nextDateStr
      if (!nextDateStr) return

      if (isToday()) {
        const nextTime = getNextAvailableTime()
        setInputValue(nextTime)
        onChange?.(nextTime)
      } else {
        setInputValue('00:00')
        onChange?.('00:00')
      }
    }
  }, [selectedDate]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return undefined

    const timer = setTimeout(() => {
      const overlays = document.querySelectorAll(`.${styles.timeDropdownOverlay}`)
      const overlay = overlays[overlays.length - 1]
      const dropdownMenu = overlay?.querySelector('.ant-dropdown-menu')

      // 현재 선택된 키 (없으면 inputValue 사용)
      const targetKey = inputValue

      if (dropdownMenu && targetKey) {
        const menuItem = dropdownMenu.querySelector(`[data-time="${targetKey}"]`)?.closest('.ant-dropdown-menu-item')
        if (menuItem) {
          dropdownMenu.scrollTop = menuItem.offsetTop - dropdownMenu.offsetTop
        }
      }
    }, 50)

    return () => {
      clearTimeout(timer)
    }
  }, [isOpen, inputValue])

  const menuItems = useMemo(() => {
    return timeOptions.map((time) => {
      const isDisabled = isTimeBeforeNow(time)
      return {
        key: time,
        label: (
          <div
            data-time={time}
            className={classNames(styles.timeOption, { [styles.disabled]: isDisabled })}
          >
            {time}
          </div>
        ),
        disabled: isDisabled,
      }
    })
  }, [selectedDate, timeOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  const dropdownMenu = useMemo(() => ({
    items: menuItems,
    onClick: handleTimeSelect,
    selectedKeys: inputValue ? [inputValue] : [],
    className: styles.timeDropdownMenu,
  }), [menuItems, inputValue]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dropdown
      menu={dropdownMenu}
      trigger={['click']}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
      overlayClassName={classNames(styles.timeDropdownOverlay, 'contextMenu')}
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      overlayStyle={{ zIndex: 2000 }}
    >
      <div className={classNames(styles.timeSelectorWrapper, className)}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={styles.timeInput}
          maxLength={5}
          {...props}
        />
        <div className={styles.dropdownButton}>
          <img src={chevronDownIcon} alt="Dropdown" className={styles.chevronIcon} />
        </div>
      </div>
    </Dropdown>
  )
}

export default TimeSelector
